import React from 'react';
import { Box, Typography, Chip, Tooltip } from '@mui/material';
import {
  LocationOn as LocationIcon,
  CalendarMonth as CalendarIcon,
  AccessTime as ClockIcon,
} from '@mui/icons-material';
import IncidentIcon from './IncidentIcon';
import { appColors } from '../../Config/Theme';
import { IncidentStatus } from '../../Types/enums';
import { formatDateTime, parseUtc } from '../../Utils/dateHelpers';
import { formatElapsedHms } from '../../Utils/elapsedTimer';
import { useNowTick } from '../../Hooks/useNowTick';

interface IncidentInfoBarProps {
  incidentName: string;
  incidentType?: string | number;
  location?: string;
  startedAtUtc?: string;
  status?: string | number;
}

const IncidentInfoBar: React.FC<IncidentInfoBarProps> = ({ incidentName, incidentType, location, startedAtUtc, status }) => {
  const isActive = status != null && (String(status).toLowerCase() === IncidentStatus.Active || status === 0);
  const now = useNowTick(isActive ? 1000 : 0);

  // Compute elapsed string for active incidents with a valid start time
  let elapsedStr: string | null = null;
  let ariaLabel: string | undefined;
  if (isActive && startedAtUtc) {
    const startMs = parseUtc(startedAtUtc).getTime();
    if (!Number.isNaN(startMs)) {
      elapsedStr = formatElapsedHms(startMs, now);
      const parts = elapsedStr.split(':');
      ariaLabel = `Active incident. Elapsed time ${parseInt(parts[0], 10)} hours, ${parseInt(parts[1], 10)} minutes, ${parseInt(parts[2], 10)} seconds.`;
    }
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
        bgcolor: appColors.surface.card,
        border: `1px solid ${appColors.border.subtle}`,
        borderRadius: '12px',
        px: 1.5,
        py: 0.75,
        mb: 0.5,
        minHeight: 52,
      }}
    >
      {/* Left cluster: icon + inline info */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 0 }}>
        {incidentType !== undefined && (
          <Box aria-hidden sx={{ flexShrink: 0, transform: 'scale(0.75)', transformOrigin: 'center' }}>
            <IncidentIcon incidentType={incidentType} />
          </Box>
        )}

        {/* Inline info row: name + location + date/time */}
        <Box
          data-testid="incident-info-row"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 1, sm: 1.5, md: 2 },
            flexWrap: 'wrap',
            minWidth: 0,
            flex: 1,
          }}
        >
          <Typography
            sx={{
              fontSize: { xs: 18, sm: 19, md: 20 },
              fontWeight: 800,
              lineHeight: 1.2,
              color: appColors.text.heading,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              minWidth: 0,
              flexShrink: 1,
            }}
          >
            {incidentName}
          </Typography>

          {location && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
              <LocationIcon aria-hidden sx={{ fontSize: 16, color: appColors.text.placeholder }} />
              <Typography sx={{ fontSize: 14, color: appColors.text.body, whiteSpace: 'nowrap', lineHeight: 1.4 }}>
                {location}
              </Typography>
            </Box>
          )}

          {startedAtUtc && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
              <CalendarIcon aria-hidden sx={{ fontSize: 16, color: appColors.text.placeholder }} />
              <Typography sx={{ fontSize: 14, color: appColors.text.caption, whiteSpace: 'nowrap', lineHeight: 1.4 }}>
                {formatDateTime(startedAtUtc)}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* Right: status chip / combined timer pill */}
      {status !== undefined && (
        isActive && elapsedStr ? (
          <Tooltip title="Elapsed since incident start" arrow>
            <Box
              aria-label={ariaLabel}
              data-testid="active-timer-pill"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                flexShrink: 0,
                borderRadius: '14px',
                height: 30,
                border: `1px solid ${appColors.status.successBorder}`,
                bgcolor: appColors.status.successBg,
                overflow: 'hidden',
              }}
            >
              {/* Active label */}
              <Typography
                component="span"
                sx={{
                  fontSize: '0.85rem',
                  fontWeight: 700,
                  letterSpacing: '0.04em',
                  color: appColors.status.success,
                  lineHeight: 1,
                  px: 1.25,
                }}
              >
                Active
              </Typography>
              {/* Divider */}
              <Box sx={{ width: '1px', height: 18, bgcolor: appColors.status.successBorder, flexShrink: 0 }} />
              {/* Timer */}
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  px: 1,
                  color: 'rgba(69, 90, 100, 0.87)',
                  fontVariantNumeric: 'tabular-nums',
                  fontSize: '0.9375rem',
                  fontWeight: 600,
                  lineHeight: 1,
                  whiteSpace: 'nowrap',
                  userSelect: 'none',
                }}
              >
                <ClockIcon sx={{ fontSize: 15, color: 'inherit', opacity: 0.7 }} />
                {elapsedStr}
              </Box>
            </Box>
          </Tooltip>
        ) : (
          <Chip
            label={isActive ? 'Active' : 'Closed'}
            size="small"
            sx={{
              flexShrink: 0,
              fontWeight: 700,
              fontSize: '0.7rem',
              letterSpacing: '0.04em',
              borderRadius: '12px',
              height: 24,
              bgcolor: isActive ? appColors.status.successBg : appColors.surface.inactive,
              color: isActive ? appColors.status.success : appColors.text.body,
              border: isActive ? `1px solid ${appColors.status.successBorder}` : `1px solid ${appColors.border.subtle}`,
            }}
          />
        )
      )}
    </Box>
  );
};

export default IncidentInfoBar;
